import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { NAV_LINKS, COMPANY, LOGO_URL } from "@/data/site";
import { CTAButton } from "@/components/shared/CTAButton";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-200/70 shadow-[0_4px_20px_rgba(10,49,97,0.06)]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="container-x flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="Agarwal Secure Packers & Movers logo" className="h-11 w-11 rounded-full object-cover ring-1 ring-brand/10" />
          <span className="flex flex-col leading-tight">
            <span className="font-heading font-extrabold text-brand text-base sm:text-lg tracking-tight">Agarwal Secure</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Packers &amp; Movers</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                  isActive ? "text-brand" : "text-gray-600 hover:text-brand"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  {isActive && <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-brand-accent rounded-full" />}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-accent transition-colors"
            data-testid="navbar-call"
          >
            <Phone className="h-4 w-4" />
            {COMPANY.mobiles[0]}
          </a>
          <CTAButton to="/#quote" size="md" arrow={false} data-testid="navbar-get-quote">
            Get Quote
          </CTAButton>
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <button
                className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-gray-200 text-brand"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-3 p-6 border-b">
                  <img src={LOGO_URL} alt="logo" className="h-10 w-10 rounded-full" />
                  <span className="font-heading font-bold text-brand">Agarwal Secure</span>
                </div>
                <div className="flex flex-col p-4 gap-1">
                  {NAV_LINKS.map((link) => (
                    <SheetClose asChild key={link.to}>
                      <NavLink
                        to={link.to}
                        end={link.to === "/"}
                        data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                        className={({ isActive }) =>
                          `px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                            isActive ? "bg-brand-light text-brand" : "text-gray-700 hover:bg-gray-50"
                          }`
                        }
                      >
                        {link.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </div>
                <div className="mt-auto p-6 border-t space-y-3">
                  <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 text-brand font-semibold" data-testid="mobile-call">
                    <Phone className="h-4 w-4" /> {COMPANY.mobiles[0]}
                  </a>
                  <SheetClose asChild>
                    <CTAButton to="/#quote" className="w-full" arrow={false}>Get Quote</CTAButton>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
